import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import { plotPriorities, plotStatuses } from '../../constants/options';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';

export default function PlotThreadForm({
  addItem,
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [plot, setPlot] = useState({ title: '', description: '', status: 'Active', priority: 'Medium' });
  const [errors, setErrors] = useState<{ title?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const normalizeOption = (value: string, options: readonly string[]): string | undefined =>
    options.find((option) => option.toLowerCase() === value.trim().toLowerCase());

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [plot.title, plot.description].some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const systemPrompt = `You draft campaign plot threads for tabletop GMs.
Return ONLY JSON with keys: title, description, status, priority.
Rules:
- status must be one of: ${plotStatuses.join(', ')}.
- priority must be one of: ${plotPriorities.join(', ')}.
- keep title concise and description practical.
- do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this plot thread draft.
Current draft:
- title: ${plot.title || '(empty)'}
- description: ${plot.description || '(empty)'}
- status: ${plot.status}
- priority: ${plot.priority}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['title', 'description', 'status', 'priority']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setPlot((previous) => ({
        ...previous,
        title: parsed.title ?? previous.title,
        description: parsed.description ?? previous.description,
        status: parsed.status ? (normalizeOption(parsed.status, plotStatuses) ?? previous.status) : previous.status,
        priority: parsed.priority ? (normalizeOption(parsed.priority, plotPriorities) ?? previous.priority) : previous.priority,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate plot draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, isDraftingWithAi, plot.description, plot.priority, plot.status, plot.title]);

  const handleSubmit = useCallback(() => {
    if (!plot.title.trim()) {
      setErrors({ title: 'Plot title is required.' });
      return;
    }
    addItem('plotThreads', plot);
    setPlot({ title: '', description: '', status: 'Active', priority: 'Medium' });
    setAiPrompt('');
    setAiError('');
    setErrors({});
  }, [addItem, plot]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Plot Thread Title</label>
        <input
          type="text"
          placeholder="Plot Thread Title"
          value={plot.title}
          onChange={(e) => {
            setPlot({ ...plot, title: e.target.value });
            if (errors.title) setErrors({});
          }}
          className={`input-field ${errors.title ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.title ? 'field-error' : 'field-hint'}>
            {errors.title || 'Required.'}
          </span>
        </div>
      </div>
      <TextAreaField
        label="Description"
        placeholder="Description"
        value={plot.description}
        onChange={(value) => setPlot({ ...plot, description: value })}
        hint="Key beats, stakes, and next steps."
        showCount
        minRows={4}
      />
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="field-block">
          <label className="field-label">Status</label>
          <select
            value={plot.status}
            onChange={(e) => setPlot({ ...plot, status: e.target.value })}
            className="input-field"
          >
            {plotStatuses.map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </div>
        <div className="field-block">
          <label className="field-label">Priority</label>
          <select
            value={plot.priority}
            onChange={(e) => setPlot({ ...plot, priority: e.target.value })}
            className="input-field"
          >
            {plotPriorities.map((priority) => (
              <option key={priority}>{priority}</option>
            ))}
          </select>
        </div>
      </div>
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Ask for stakes, antagonist pressure, and next escalation beats."
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Plot Thread
        </button>
      )}
    </div>
  );
}
